import type { ReactNode } from "react";
import { useEffect, useRef, useState, isValidElement } from "react";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCenter,
  type DragStartEvent, type DragOverEvent, type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, arrayMove, rectSortingStrategy,
} from "@dnd-kit/sortable";
import { X, Check, Plus, Info } from "lucide-react";
import { Tip } from "./ui/tooltip";

/* ============================================================================
   ConfigurableCardGrid — reusable smartphone-home-screen card grid.

   Drag any card (in edit mode) and the others move aside LIVE to make room,
   like rearranging apps on a phone. Reflow happens on hover and settles on
   drop. Built for MIXED-SIZE cards without the usual mess:

     • the list is reordered live on drag-over, so the CSS grid itself re-packs
       the cards — no transform maths fighting the layout (nothing scales or
       overlaps);
     • the dragged card's slot becomes a "hole" that follows the cursor, while a
       DragOverlay renders a clean floating copy at its exact natural size.

   Order IS the layout: dropping reorders the list and we hand it back up via
   onReorder (parent persists it). Each dashboard supplies its own card sizes
   via `sizeOf`, so the same grid powers the group and org dashboards.
   ========================================================================== */

export type CardSize = "sm" | "md" | "wide" | "full";

/* Fine row unit so card heights pack densely; gap matches CSS `gap`.
   height = rows*ROW_UNIT + (rows-1)*GAP  →  sm≈88  md/wide≈400  full≈452 */
const ROW_UNIT = 10; // px per implicit grid row
const GAP = 16;      // px gap between cards
const ROW_SPAN: Record<CardSize, number> = { sm: 4, md: 16, wide: 16, full: 18 };

/* Column span per card size at a given column count (5 / 2 / 1). */
function colSpanFor(size: CardSize, cols: number): number {
  if (cols <= 1) return 1;
  if (cols === 2) return size === "sm" ? 1 : 2;
  switch (size) {            // multi-column desktop grid
    case "sm":   return 1;
    case "md":   return 2;
    /* A wide card fills everything except the 2 columns an `md` needs, so a
       wide + md pair always fills one row exactly — 5 cols → 3+2, 6 cols → 4+2.
       (Without this, a 6-column grid left a 1-column gap beside the chart pair.) */
    case "wide": return Math.max(3, cols - 2);
    case "full": return cols; // span the whole row whatever the column count is
    default:     return 1;
  }
}

/* Responsive column count from the grid's own width (not the viewport).
   `desktop` is the chosen desktop column count (adapts to the KPI count). */
function colsForWidth(w: number, desktop: number): number {
  if (w >= 1024) return desktop;
  if (w >= 640) return 2;
  return 1;
}

export function ConfigurableCardGrid({
  items, renderMap, editing, onRemove, onReorder, sizeOf,
  selectable = false, checked, onToggleCheck, renderAbout, highlightId,
}: {
  items: string[];
  renderMap: Record<string, ReactNode>;
  editing: boolean;
  onRemove: (id: string) => void;
  onReorder: (ids: string[]) => void;
  sizeOf: (id: string) => CardSize | undefined;
  /* Select mode (Extra-cards editor): show a checkbox + dim instead of the
     remove button; clicking a card toggles its check, dragging reorders. */
  selectable?: boolean;
  checked?: Set<string>;
  onToggleCheck?: (id: string) => void;
  /* Optional "about" face — when provided, each tile gets an (i) button that
     flips the card to show this node (used in select mode). */
  renderAbout?: (id: string) => ReactNode;
  /* Spotlight a single card (deep-linked from "View detail"): that card is
     ringed and scrolled into view while the rest are blurred/dimmed. Clicks are
     suppressed while active so a tap anywhere can dismiss it (handled upstream). */
  highlightId?: string;
}) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(5);

  /* Tier of a card: KPI cards (sm) live in the top rows, every other card is a
     widget below. Cards may only be reordered within their own tier so a widget
     can never sit among the KPI cards (and vice versa). */
  const groupOf = (id: string) => (sizeOf(id) === "sm" ? "kpi" : "widget");
  /* Keep all KPI cards first, widgets after (stable within each tier) so the
     KPI row always stays on top regardless of how cards were added/saved. */
  const groupSort = (ids: string[]) =>
    [...ids].sort((a, b) => (groupOf(a) === "kpi" ? 0 : 1) - (groupOf(b) === "kpi" ? 0 : 1));

  /* Live order during a drag (synced from props while idle). */
  const [order, setOrder] = useState<string[]>(() => groupSort(items));
  const orderRef = useRef(order);
  orderRef.current = order;
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overlay, setOverlay] = useState<{ w: number; h: number } | null>(null);
  /* True while the dragged card hovers a forbidden tier → red "no drop" cue. */
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (!activeId) setOrder(groupSort(items));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, activeId]);

  /* Desktop column count auto-adjusts to the number of KPI (sm) cards so the top
     row always fills evenly — 5 KPIs → 5 columns, 6 → 6 — capped at 6, min 5. */
  const desktopCols = Math.min(6, Math.max(5, items.filter((id) => sizeOf(id) === "sm").length));

  /* Track the grid's width → choose desktop / 2 / 1 columns. */
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const update = () => setCols(colsForWidth(el.clientWidth, desktopCols));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [desktopCols]);

  /* Spotlight deep-link: scroll the highlighted card into view when it changes. */
  useEffect(() => {
    if (!highlightId) return;
    const el = gridRef.current?.querySelector<HTMLElement>(`[data-card-id="${CSS.escape(highlightId)}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  /* Distinguish a real drag from a click (for select-mode toggling): set on
     drag start, cleared a tick after drag end so the trailing click is ignored. */
  const draggedRef = useRef(false);
  const handleCardClick = (id: string) => {
    if (!selectable) return;
    if (draggedRef.current) return; // this "click" came from finishing a drag
    if (highlightId) return;        // spotlight active → a click just dismisses it (upstream)
    onToggleCheck?.(id);
  };

  const onDragStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    draggedRef.current = true;
    setActiveId(id);
    setInvalid(false);
    
    /* Size the floating clone exactly from its actual DOM size if available,
       otherwise fallback to calculated footprint. */
    const el = gridRef.current?.querySelector(`[data-card-id="${CSS.escape(id)}"]`);
    if (el) {
      setOverlay({
        w: el.clientWidth,
        h: el.clientHeight,
      });
    } else {
      const size = sizeOf(id) ?? "sm";
      const gw = gridRef.current?.clientWidth ?? 0;
      const cspan = colSpanFor(size, cols);
      const colW = cols > 0 ? (gw - (cols - 1) * GAP) / cols : gw;
      const rspan = ROW_SPAN[size];
      setOverlay({
        w: colW * cspan + (cspan - 1) * GAP,
        h: ROW_UNIT * rspan + (rspan - 1) * GAP,
      });
    }
  };

  /* Reorder live so the other cards move aside as you drag over them — but only
     within the same tier. Crossing tiers (KPI ↔ widget) is rejected and flagged
     invalid so the floating card shows a red "can't drop here" cue (the lock that
     keeps widgets out of the KPI row and vice versa). */
  const onDragOver = (e: DragOverEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) { setInvalid(false); return; }
    if (groupOf(String(active.id)) !== groupOf(String(over.id))) {
      setInvalid(true);
      return; // forbidden tier → no reorder
    }
    setInvalid(false);
    setOrder((prev) => {
      const from = prev.indexOf(String(active.id));
      const to = prev.indexOf(String(over.id));
      if (from === -1 || to === -1) return prev;
      return arrayMove(prev, from, to);
    });
  };

  const onDragEnd = (_e: DragEndEvent) => {
    setActiveId(null);
    setOverlay(null);
    setInvalid(false);
    onReorder(orderRef.current);
    setTimeout(() => { draggedRef.current = false; }, 0);
  };
  const onDragCancel = () => {
    setActiveId(null);
    setOverlay(null);
    setInvalid(false);
    setOrder(groupSort(items));
    setTimeout(() => { draggedRef.current = false; }, 0);
  };

  const activeSize = activeId ? sizeOf(activeId) : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <SortableContext items={order} strategy={rectSortingStrategy}>
        <div
          ref={gridRef}
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            gridAutoRows: `${ROW_UNIT}px`,
            /* Normal row flow (NOT dense): keeps the dragged card's moving slot
               in line with the cursor so live reordering stays stable and
               doesn't flicker/oscillate. */
            gridAutoFlow: "row",
            gap: `${GAP}px`,
          }}
        >
          {order.map((id) => {
            const size = sizeOf(id);
            if (!size) return null;
            return (
              <SortableCard
                key={id}
                id={id}
                size={size}
                cols={cols}
                editing={editing}
                dragging={id === activeId}
                onRemove={() => onRemove(id)}
                selectable={selectable}
                isChecked={checked?.has(id) ?? false}
                onCardClick={() => handleCardClick(id)}
                about={renderAbout?.(id)}
                highlighted={highlightId === id}
                dimmed={!!highlightId && highlightId !== id}
              >
                {renderMap[id]}
              </SortableCard>
            );
          })}
        </div>
      </SortableContext>

      {/* Floating clone that follows the cursor at the card's natural size.
          Turns red over a forbidden tier to signal the drop isn't allowed. */}
      <DragOverlay>
        {activeId && activeSize ? (
          <div
            style={overlay ? { width: overlay.w, height: overlay.h } : undefined}
            className={`relative overflow-hidden rounded-2xl ${
              invalid
                ? "cursor-not-allowed"
                : "cursor-grabbing ring-1 ring-indigo-500/30 shadow-[0_16px_40px_-16px_rgba(0,0,0,0.45)]"
            }`}
          >
            <div className="pointer-events-none h-full select-none">{renderMap[activeId]}</div>
            {/* Forbidden tier → just a gradient-red border (no heavy glow/fill). */}
            {invalid && (
              <div
                className="pointer-events-none absolute inset-0 rounded-2xl p-[2px]"
                style={{
                  background: "linear-gradient(135deg, #ef4444, #f43f5e)",
                  WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  WebkitMaskComposite: "xor",
                  maskComposite: "exclude",
                }}
              />
            )}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/* One draggable tile. The whole card is the drag handle in edit mode; the
   remove button opts out via pointer-down stopPropagation. We do NOT apply the
   sortable transform — the live array reorder + CSS grid handle the reflow, so
   nothing scales or overlaps. */
function SortableCard({
  id, size, cols, editing, dragging, onRemove, selectable, isChecked, onCardClick, about,
  highlighted = false, dimmed = false, children,
}: {
  id: string;
  size: CardSize;
  cols: number;
  editing: boolean;
  dragging: boolean;
  onRemove: () => void;
  selectable: boolean;
  isChecked: boolean;
  onCardClick: () => void;
  about?: ReactNode;
  highlighted?: boolean;
  dimmed?: boolean;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef } = useSortable({ id, disabled: !editing });
  const [rowSpan, setRowSpan] = useState(ROW_SPAN[size]);
  const contentRef = useRef<HTMLDivElement>(null);
  const isDynamic = size === "full";

  useEffect(() => {
    if (!isDynamic) {
      setRowSpan(ROW_SPAN[size]);
      return;
    }

    const el = contentRef.current;
    if (!el) return;

    const update = () => {
      const height = el.scrollHeight;
      const calculated = Math.ceil((height + GAP) / (ROW_UNIT + GAP));
      if (calculated > 0 && calculated !== rowSpan) {
        setRowSpan(calculated);
      }
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [size, isDynamic, rowSpan]);

  const style: React.CSSProperties = {
    gridColumn: `span ${colSpanFor(size, cols)}`,
    gridRow: `span ${rowSpan}`,
  };

  return (
    <div
      ref={setNodeRef}
      data-card-id={id}
      style={style}
      onClick={selectable ? onCardClick : undefined}
      className={`relative h-full overflow-hidden rounded-2xl transition-all duration-300 ${editing ? "cursor-move" : ""} ${
        selectable && !dragging
          ? isChecked
            ? "ring-2 ring-indigo-500"
            : "ring-1 ring-border"
          : ""
      } ${highlighted ? "z-20 scale-[1.02] ring-2 ring-indigo-500 shadow-2xl shadow-indigo-500/30" : ""} ${
        dimmed ? "pointer-events-none blur-[3px] opacity-40" : ""
      }`}
      {...(editing ? { ...attributes, ...listeners } : {})}
    >
      {/* While this card is the one being dragged, its slot becomes an empty
          dashed "hole" that follows the cursor; the overlay shows the card. */}
      {dragging ? (
        <div className="h-full rounded-2xl border-2 border-dashed border-indigo-500/60 bg-indigo-500/5" />
      ) : selectable ? (
        <div className="group/card h-full">
          {/* live card front (no flip). Hide the cards' gradient top accent here
              so the Extra-cards selector shows just a clean border. Dynamic
              (full-width) cards size to their content here too — so a table
              preview shrinks to its rows instead of stretching to a fixed slot. */}
          <div
            ref={contentRef}
            className={`pointer-events-none [&_.kpi-accent]:hidden ${isDynamic ? "h-auto" : "h-full"}`}
          >
            {children}
          </div>
          {/* dim the unselected cards */}
          {!isChecked && <div className="pointer-events-none absolute inset-0 bg-background/40" />}

          {/* centered controls — revealed on hover. Clicking the card toggles
              selection; the (i) opens a tooltip with the card's details. */}
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center gap-2 bg-background/0 opacity-0 transition-all duration-200 group-hover/card:bg-background/55 group-hover/card:opacity-100">
            {/* Add / Added pill — click toggles (bubbles to the card) */}
            <span
              className={`pointer-events-auto inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-bold shadow-lg transition-colors ${
                isChecked
                  ? "border-indigo-500 bg-indigo-500 text-white"
                  : "border-white/20 bg-card/95 text-foreground backdrop-blur hover:border-indigo-400/50"
              }`}
            >
              {isChecked ? <Check className="size-4" /> : <Plus className="size-4" />}
              {isChecked ? "Added" : "Add"}
            </span>

            {/* info icon → details in a real tooltip (portaled outside the card) */}
            {about && (
              <Tip label={about} side="top" className="pointer-events-auto inline-flex cursor-pointer">
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  aria-label="About this card"
                  className="flex size-9 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-card/95 text-muted-foreground shadow-lg backdrop-blur transition-colors hover:text-foreground"
                >
                  <Info className="size-4" />
                </button>
              </Tip>
            )}
          </div>
        </div>
      ) : (
        <>
          {editing && (
            <>
              <div className="pointer-events-none absolute inset-0 z-10 rounded-2xl border-2 border-dashed border-indigo-500/40" />
              {/* Remove button (drag from anywhere on the card) */}
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={onRemove}
                title="Remove card"
                className="absolute right-2 top-2 z-20 flex size-7 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:border-red-500/40 hover:text-red-500"
              >
                <X className="size-4" />
              </button>
            </>
          )}
          <div
            ref={contentRef}
            className={`${isDynamic ? "h-auto" : "h-full"} ${editing ? "pointer-events-none select-none" : ""}`}
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
}
